import type { FC } from "react";
import { useMemo, useState } from "react";

import { useStoreLocatorContext } from "@locators/components/shared/hooks/useStoreLocatorContext";
import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import type { StoreLocatorOpeningHours } from "@jti/fe-common";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { StoreLocatorOpeningStatus } from "../../../StoreLocatorOpeningStatus/StoreLocatorOpeningStatus";

import { StoreLocatorDayShopSchedule } from "./components/StoreLocatorDayShopSchedule";

import { getCurrentDayShopStatus, getDayOfWeekMondayFirst, getNextDayOpenTime } from "./utils";

import styles from "./StoreLocatorShopSchedule.module.scss";

type StoreLocatorShopScheduleProps = {
  isListView: boolean;
  shopSchedule: StoreLocatorOpeningHours;
  isFoldable?: boolean;
  forceOpeningHoursBreaks?: boolean;
};

export const shopStatus = {
  open: "open",
  closed: "closed",
  open_soon: "open_soon",
} as const;

export const StoreLocatorShopSchedule: FC<StoreLocatorShopScheduleProps> = ({
  shopSchedule,
  isListView,
  isFoldable = false,
  forceOpeningHoursBreaks,
}) => {
  const { __ } = useTranslations();
  const { isOpeningHoursBreaksEnabled: contextBreaksEnabled } = useStoreLocatorContext();
  const { days, time_zone } = shopSchedule;
  const dayOfWeekMondayFirst = getDayOfWeekMondayFirst();
  const currentDayShopSchedule = days[dayOfWeekMondayFirst];
  const nextDayOpenTime = useMemo(() => getNextDayOpenTime(days), [days]);
  const [isExpanded, setIsExpanded] = useState(!isFoldable);

  const isOpeningHoursBreaksEnabled = forceOpeningHoursBreaks === true ? true : (contextBreaksEnabled ?? false);

  const { shopOpeningStatus, shopOpeningHours } = useMemo(
    () =>
      getCurrentDayShopStatus(
        currentDayShopSchedule,
        time_zone ?? "",
        isOpeningHoursBreaksEnabled,
        isListView,
        nextDayOpenTime ?? ""
      ),
    [currentDayShopSchedule, time_zone, isOpeningHoursBreaksEnabled, isListView, nextDayOpenTime]
  );

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const getTranslatedDayName = (dayName: string): string => {
    const translationKey = `store_locator.popup.shop_status.day_name.${dayName.toLowerCase()}`;
    const translatedName = __(translationKey);

    return translatedName !== translationKey ? translatedName : dayName;
  };

  return (
    <>
      <StoreLocatorOpeningStatus shopOpeningStatus={shopOpeningStatus} shopOpeningHours={shopOpeningHours} />
      {!isListView && (
        <>
          {isExpanded && (
            <div className={styles.shopScheduleWrapper}>
              {days.map((day) => (
                <div
                  className={classNames(styles.shopDaySchedule, {
                    [styles.currentDay]: day.name === currentDayShopSchedule.name,
                  })}
                  data-testid="shopDaySchedule"
                  key={day.name}
                >
                  <div>{getTranslatedDayName(day.name)}</div>
                  {day.closed ? (
                    <>{__(`store_locator.popup.shop_status.${shopStatus.closed}`)}</>
                  ) : (
                    <StoreLocatorDayShopSchedule day={day} isOpeningHoursBreaksEnabled={isOpeningHoursBreaksEnabled} />
                  )}
                </div>
              ))}
            </div>
          )}
          {isFoldable && (
            <Button variant="text" size="md" onClick={toggleExpanded} data-testid="scheduleToggle">
              {__(isExpanded ? "product.info.readLess" : "product.info.readMore")}
            </Button>
          )}
        </>
      )}
    </>
  );
};
